import {YouTubeThumbnailCollectionType} from "../../definitions/types";
import {YouTubeThumbnailModel} from "./youTubeThumbnailModel";

const defaultThumbnail = new YouTubeThumbnailModel({
    url: 'https://static.commerceplatform.services/content/site/hs1/img/d2g-video-poster-small.jpg'
});

export class YouTubeThumbnailCollectionModel {
    private readonly _defaultResolution: YouTubeThumbnailModel;
    private readonly _mediumResolution: YouTubeThumbnailModel;
    private readonly _highResolution?: YouTubeThumbnailModel;
    private readonly _standardResolution?: YouTubeThumbnailModel;
    private readonly _maxResolution?: YouTubeThumbnailModel;

    constructor({
                    defaultResolution,
                    mediumResolution,
                    highResolution,
                    standardResolution,
                    maxResolution
                }: YouTubeThumbnailCollectionType) {
        this._defaultResolution = new YouTubeThumbnailModel(defaultResolution);
        this._mediumResolution = new YouTubeThumbnailModel(mediumResolution);
        this._highResolution = new YouTubeThumbnailModel(highResolution);
        this._standardResolution = standardResolution && new YouTubeThumbnailModel(standardResolution);
        this._maxResolution = maxResolution && new YouTubeThumbnailModel(maxResolution);
    }

    /**
     * @property defaultResolution
     * @description The default thumbnail image.
     * The default thumbnail for a video – or a resource that refers to a video,
     * such as a playlist item or search result – is 120px wide and 90px tall.
     * @returns {YouTubeThumbnailModel}
     */
    public get defaultResolution(): YouTubeThumbnailModel {
        return this._defaultResolution;
    }

    /**
     * @property mediumResolution
     * @description A higher resolution version of the thumbnail image.
     * For a video (or a resource that refers to a video), this image is 320px wide and 180px tall.
     * @returns {YouTubeThumbnailModel}
     */
    public get mediumResolution(): YouTubeThumbnailModel {
        return this._mediumResolution;
    }

    /**
     * @property highResolution
     * @description A high-resolution version of the thumbnail image.
     * For a video (or a resource that refers to a video), this image is 480px wide and 360px tall.
     * @returns {YouTubeThumbnailModel | undefined}
     */
    public get highResolution(): YouTubeThumbnailModel | undefined {
        return this._highResolution;
    }

    /**
     * @property standardResolution
     * @description (Optional) An even higher resolution version of the thumbnail image than the high-resolution image.
     * This image is available for some videos and other resources that refer to videos,
     * like playlist items or search results.
     * @returns {YouTubeThumbnailModel}
     */
    public get standardResolution(): YouTubeThumbnailModel | undefined {
        return this._standardResolution;
    }

    /**
     * @property maxResolution
     * @description (Optional) The highest resolution version of the thumbnail image.
     * This image size is available for some videos and other resources that refer to videos,
     * like playlist items or search results.
     * This image is 1280px wide and 720px tall.
     * @returns {YouTubeThumbnailModel}
     */
    public get maxResolution(): YouTubeThumbnailModel | undefined {
        return this._maxResolution;
    }

    /**
     * @property poster
     * @description The poster image, which is used to provide the user with a preview of the video.
     * Order of precedence: maxResolution, standardResolution, highResolution, defaultResolution
     * @returns {YouTubeThumbnailModel}
     */
    public get poster(): YouTubeThumbnailModel {
        return this._maxResolution || this._standardResolution || this._highResolution || defaultThumbnail
    }
}